import { forwardRef, type ReactNode } from 'react'

import { ListCard } from '@/components/list-card'
import { PersonListCardIdentity } from '@/components/person-list-card-identity'
import type { ListCardHeaderBadge } from '@/components/list-card-header'
import type { PersonListCardModel, PersonListCardVariant } from '@/lib/person-list-card-model'
import { cn } from '@/lib/utils'

export type { PersonListCardModel, PersonListCardVariant } from '@/lib/person-list-card-model'

export type PersonListCardProps = {
  variant?: PersonListCardVariant
  person: PersonListCardModel
  headerAside?: ReactNode
  meta?: ReactNode
  body?: ReactNode
  footer?: ReactNode
  /** Pipeline overflow menu — header-right, stops propagation. */
  menu?: ReactNode
  onOpen?: () => void
  openAriaLabel?: string
  agentHighlight?: string
  className?: string
  bodyClassName?: string
  footerClassName?: string
  embedded?: boolean
  /** Kanban drag overlay styling. */
  isOverlay?: boolean
  /** Spread onto the outer card surface (e.g. dnd-kit listeners). */
  surfaceProps?: Record<string, unknown>
  titleFallback?: ReactNode
  titleBadge?: ListCardHeaderBadge | null
}

/**
 * Shared person row shell on `ListCard`: round avatar, title block, optional slots.
 * Scenario content (skills, match reasoning, invite CTA) is caller-owned via slots.
 */
export const PersonListCard = forwardRef<HTMLDivElement, PersonListCardProps>(function PersonListCard(
  {
    variant = 'standard',
    person,
    headerAside,
    meta,
    body,
    footer,
    menu,
    onOpen,
    openAriaLabel,
    agentHighlight,
    className,
    bodyClassName,
    footerClassName,
    embedded,
    isOverlay = false,
    surfaceProps,
    titleFallback,
    titleBadge,
  },
  ref,
) {
  const isPipeline = variant === 'pipeline'

  return (
    <ListCard
      ref={ref}
      className={cn(
        'flex min-h-0 min-w-0 w-full flex-col',
        variant === 'standard' || variant === 'evaluation'
          ? 'transition-shadow hover:shadow-md'
          : null,
        isPipeline &&
          cn(
            'group relative cursor-pointer transition-colors hover:shadow-none',
            'hover:border-foreground/20 hover:bg-muted/30',
            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
          ),
        isOverlay && 'shadow-lg ring-1 ring-border/80',
        className,
      )}
      embedded={embedded}
      onOpen={onOpen}
      openAriaLabel={openAriaLabel}
      agentHighlight={agentHighlight}
      bodyClassName={bodyClassName}
      footerClassName={footerClassName}
      surfaceProps={surfaceProps}
      header={
        <PersonListCardIdentity
          person={person}
          variant={variant}
          headerAside={headerAside}
          meta={meta}
          menu={menu}
          titleFallback={titleFallback}
          titleBadge={titleBadge}
        />
      }
      body={body}
      footer={footer}
    />
  )
})

PersonListCard.displayName = 'PersonListCard'
